import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import {
  Paper,
  Button,
  Box,
  TextField,
  Snackbar,
  Alert,
  FormControl,
  InputLabel
} from '@mui/material';

import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

interface PBM {
  id: number;
  pbmName: string;
}

const PBMTable = () => {
  const navigate = useNavigate();
  const [pbms, setPbms] = useState<PBM[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [feedback, setFeedback] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'pbmName',
      sort: 'asc',
    },
  ]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 100 },
    {
      field: 'pbmName',
      headerName: 'PBM Name',
      width: 300,
      renderCell: (params: GridRenderCellParams<PBM>) => (
        <Button
          sx={{ textTransform: 'none' }}
          onClick={() => navigate(`/pbm/${params.row.id}`)}
        >
          {params.row.pbmName}
        </Button>
      )
    }
  ];

  const fetchPBMs = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<PBM[]>('/api/PBM');
      setPbms(data);
    } catch (error) {
      console.error("[PBMList] Error fetching data from /api/PBM:", error);
      setFeedback({
        open: true,
        message: 'Failed to fetch PBMs. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPBMs();
  }, []);

  const handleCloseFeedback = () => {
    setFeedback(prev => ({ ...prev, open: false }));
  };

  const filteredPBMs = pbms.filter(pbm =>
    pbm.pbmName.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>
              Search PBM Name
            </InputLabel>
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Type to search..."
            />
          </FormControl>
        </Box>

        <DataGrid
          rows={filteredPBMs}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          pageSizeOptions={[10, 25, 50]}
          autoHeight
          sx={{ cursor: 'pointer' }}
        />

      </Paper>

      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PBMTable;
